/**
 * Get Distribution History Use Case
 *
 * This use case handles the business logic for retrieving distribution history.
 * It sorts records by most recent first and applies business rules.
 *
 * @module GetDistributionHistoryUseCase
 */

import {DistributionRepository} from '../repository/DistributionRepository';
import {DistributionRecord, DistributionFilters} from '../models/distribution.model';
import {Pagination} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

export class GetDistributionHistoryUseCase {
  constructor (private distributionRepository: DistributionRepository) {}

  /**
   * Execute the use case to get distribution history
   * @param filters - Optional filters to apply
   * @param pagination - Optional pagination options
   * @returns Promise with distribution history data
   */
  async execute (filters?: DistributionFilters, page = 1, limit: number = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<{ items: DistributionRecord[], pagination?: Pagination }> {
    try {
      const result = await this.distributionRepository.getDistributionHistory(filters, page, limit);

      // Business Logic: Sort by most recent first
      const sortedRecords = result.items.sort((a, b) =>
        new Date(b.deliveredAt).getTime() - new Date(a.deliveredAt).getTime()
      );

      return {
        items: sortedRecords,
        pagination: result.pagination
      };
    } catch (error: unknown) {
      // Re-throw the error to be handled by the caller
      throw error;
    }
  }
}
